import { useState } from 'react';
import { Robot, Stop, Play, Eye, CircleNotch, WifiSlash, ArrowSquareOut, GitPullRequest } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '../lib/api';
import { extractErrorMessage } from '../lib/errors';
import { useT } from '../i18n/I18nContext';
import { useAuth } from '../context/AuthContext';
import { AGENT_BADGE_STYLES, canUseAi, isAgentOffline, isAgentRunning, useAgentClock } from '../lib/agent';

// The Claude agent block of the ticket panel: «AI» / «Стоп» / «Превью», live state,
// MR links and the step log (agent_step events collected by TicketDetail).
export default function AgentPanel({ ticket, project, steps, onTicket }) {
  const t = useT();
  const { user } = useAuth();
  const [busy, setBusy] = useState(null);
  const agent = ticket.agent;
  const running = isAgentRunning(agent);
  useAgentClock(running);
  const offline = isAgentOffline(agent);
  const enabled = !!project?.agent_enabled;
  const allowed = canUseAi(project, user?.id);
  const repos = project?.repos || [];
  const repoName = (id) => repos.find((r) => r.id === id)?.name || id.slice(0, 8);
  const mrUrls = Object.entries(agent?.mr_urls || {});
  const hasBranch = Object.keys(agent?.branches || {}).length > 0;
  const preview = agent?.preview || { state: 'none' };

  if (!agent && !enabled) {
    return (
      <div className="border border-dashed border-zinc-200 rounded-md p-3 text-[11px] text-zinc-400" data-testid="agent-panel-disabled">
        {t('agent.disabledHint')}
      </div>
    );
  }

  const act = async (action, toastKey) => {
    setBusy(action);
    try {
      const { data } = await api.post(`/tickets/${ticket.id}/agent/${action}`);
      onTicket(data);
      toast.success(t(toastKey));
    } catch (err) {
      toast.error(extractErrorMessage(err, t('agent.actionFailed')));
    } finally {
      setBusy(null);
    }
  };

  const btn = 'inline-flex items-center gap-1 px-2 py-1 rounded-sm text-xs font-medium disabled:opacity-50';

  return (
    <div className="border border-zinc-200 rounded-md p-3 space-y-2" data-testid="agent-panel">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Robot size={14} weight="bold" className="text-zinc-500 shrink-0" />
          <span className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500">{t('agent.title')}</span>
          {agent && (
            <span
              data-testid="agent-panel-state"
              className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 border rounded-sm ${
                offline ? 'bg-zinc-100 text-zinc-500 border-zinc-200' : AGENT_BADGE_STYLES[agent.state]
              }`}
            >
              {offline ? <WifiSlash size={10} weight="bold" /> : running && <CircleNotch size={10} weight="bold" className="animate-spin" />}
              {offline ? t('agent.offline') : t(`agent.state.${agent.state}`)}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {allowed && hasBranch && (
            <button
              type="button"
              data-testid="agent-preview-btn"
              onClick={() => act('preview', 'agent.previewToast')}
              disabled={!!busy || preview.state === 'starting'}
              className={`${btn} border border-zinc-200 text-zinc-700 hover:bg-zinc-50`}
            >
              <Eye size={12} weight="bold" /> {t('agent.preview')}
            </button>
          )}
          {!allowed ? (
            enabled && <span className="text-[11px] text-zinc-400" data-testid="agent-no-access">{t('agent.noAccess')}</span>
          ) : running ? (
            <button
              type="button"
              data-testid="agent-stop-btn"
              onClick={() => act('stop', 'agent.stoppedToast')}
              disabled={!!busy}
              className={`${btn} border border-red-200 text-red-600 hover:bg-red-50`}
            >
              <Stop size={12} weight="bold" /> {t('agent.stop')}
            </button>
          ) : enabled && (
            <button
              type="button"
              data-testid="agent-start-btn"
              title={t('agent.startHint')}
              onClick={() => act('start', 'agent.startedToast')}
              disabled={!!busy}
              className={`${btn} bg-black text-white hover:bg-zinc-800`}
            >
              {busy === 'start' ? <CircleNotch size={12} weight="bold" className="animate-spin" /> : <Play size={12} weight="fill" />}
              {agent?.session_id ? t('agent.resume') : t('agent.start')}
            </button>
          )}
        </div>
      </div>

      {agent?.step && <p className="text-xs text-zinc-700" data-testid="agent-panel-step">{agent.step}</p>}
      {agent?.state === 'failed' && agent.last_error && (
        <p className="text-xs text-red-600 whitespace-pre-wrap">{t('agent.lastError')}: {agent.last_error}</p>
      )}

      {(mrUrls.length > 0 || preview.state !== 'none') && (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          {mrUrls.map(([repoId, url]) => (
            <a
              key={repoId}
              href={url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 font-mono text-[#0055ff] hover:underline"
            >
              <GitPullRequest size={12} weight="bold" /> {t('agent.mr')} {repoName(repoId)}
            </a>
          ))}
          {preview.state === 'starting' && <span className="text-zinc-500">{t('agent.previewStarting')}</span>}
          {preview.state === 'failed' && <span className="text-red-600">{t('agent.previewFailed')}</span>}
          {(preview.state === 'running' || preview.state === 'sleeping') && preview.url && (
            <a
              href={preview.url}
              target="_blank"
              rel="noreferrer"
              data-testid="agent-preview-link"
              className="inline-flex items-center gap-1 text-[#0055ff] hover:underline"
              title={preview.state === 'sleeping' ? t('agent.previewSleeping') : undefined}
            >
              <ArrowSquareOut size={12} weight="bold" /> {t('agent.openPreview')}
            </a>
          )}
        </div>
      )}

      {steps.length > 0 && (
        <details open={running}>
          <summary className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500 cursor-pointer select-none">
            {t('agent.log')}
          </summary>
          <ol className="mt-1 space-y-0.5 max-h-40 overflow-y-auto" data-testid="agent-step-log">
            {steps.map((s, i) => (
              <li key={`${s.at}-${i}`} className="flex gap-2 text-[11px]">
                <span className="font-mono text-zinc-400 shrink-0">{new Date(s.at).toLocaleTimeString()}</span>
                <span className="text-zinc-700">{s.step || t(`agent.state.${s.state}`)}</span>
              </li>
            ))}
          </ol>
        </details>
      )}
    </div>
  );
}
